import React, { useEffect, useMemo, useState } from "react";

import { Stack } from "@mui/system";
import { format, milliseconds } from "date-fns";
import { useHistory, useLocation } from "react-router-dom";

import useGetTradeProposalBundles, {
  Pagination,
  QueryString,
} from "@fartherfinance/frontend/api/Investment/hooks/useGetTradeProposalBundles";

import {
  BundleFilter,
  BundleFilterOptions,
  BundleLocationState,
} from "../types";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import Select from "@src/sharedComponents/Select/Select";
import PaginatedTable from "@src/sharedComponents/Table/PaginatedTable";
import { Cell, Column, Row } from "@src/sharedComponents/Table/Types";

const UserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const EasternTimeZone = "America/New_York";

const UseEastern = true;

const timeSuffix = UseEastern ? " (East)" : " (Local)";

type Keys =
  | "bundleId"
  | "bundleType"
  | "createdAt"
  | "updatedAt"
  | "expiresAt"
  | "tradeProposalsCount"
  | "status";

const columns: Column<Keys>[] = [
  { accessor: "bundleId", label: "Bundle Id", sortable: false },
  { accessor: "bundleType", label: "Bundle Type", sortable: false },
  { accessor: "createdAt", label: "Created", sortable: false },
  { accessor: "updatedAt", label: "Updated", sortable: false },
  { accessor: "expiresAt", label: "Expired", sortable: false },
  {
    accessor: "tradeProposalsCount",
    label: "Proposals Count",
    sortable: false,
  },
  { accessor: "status", label: "Status", sortable: false },
];

const ProposalBundlesTable: React.FC = () => {
  const { state } = useLocation<BundleLocationState>();
  const [page, setPage] = useState(state?.page ?? 1);
  const [pageSize, _setPageSize] = useState(20);
  const history = useHistory<BundleLocationState>();

  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  const [proposalFilter, setProposalFilter] = useState<BundleFilter>(
    state?.proposalFilter ?? "Active Bundles"
  );

  const activeBundles = useMemo<Partial<QueryString>>(
    () => ({
      expiresAtGt: "now",
      bundleStatus: ["Draft"],
      orderBy: "createdAt asc",
    }),
    []
  );

  const submittedBundles = useMemo<Partial<QueryString>>(
    () => ({
      bundleStatus: ["Submitted"],
      orderBy: "createdAt desc",
    }),
    []
  );

  const expiredBundles = useMemo<Partial<QueryString>>(
    () => ({
      // expiresAtLt: "now",
      bundleStatus: ["Draft"],
      orderBy: "createdAt desc",
    }),
    []
  );

  const allBundles = useMemo<Partial<QueryString>>(
    () => ({
      orderBy: "createdAt desc",
    }),
    []
  );

  const queryString = useMemo<QueryString>(() => {
    const base: Pagination = {
      page,
      pageSize,
      orderBy: "createdAt desc",
    };

    switch (proposalFilter) {
      case "Active Bundles":
        return { ...base, ...activeBundles };

      case "Submitted Bundles":
        return { ...base, ...submittedBundles };

      case "Expired Bundles":
        return { ...base, ...expiredBundles };

      case "All Bundles":
        return { ...base, ...allBundles };
    }
  }, [
    activeBundles,
    allBundles,
    expiredBundles,
    page,
    pageSize,
    proposalFilter,
    submittedBundles,
  ]);

  const auth = useAdvisorRequestAuth();

  const proposalBundles = useGetTradeProposalBundles(queryString, auth, false, {
    refetchInterval: milliseconds({ seconds: 10 }),
  });

  useEffect(() => {
    setUpdatedAt(new Date());
  }, [proposalBundles]);

  const data = useMemo((): Row<Keys, Cell>[] => {
    if (proposalBundles.data === undefined) {
      return [];
    }

    return proposalBundles.data.data.map((proposalBundle) => ({
      onClick: () => {
        const newUrl = `/Advisor/FinancialOperations/TradeProposalBundles/${proposalBundle.tradeProposalBundleId}`;

        history.push(newUrl, {
          proposalFilter,
          page,
        });
      },
      key: proposalBundle.tradeProposalBundleId,
      bundleId: proposalBundle.tradeProposalBundleId,
      bundleType:
        proposalBundle.type === "Standard"
          ? "Standard"
          : proposalBundle.type === "RequiresAdditionalReview"
          ? "Requires Additional Review"
          : "Unknown",
      createdAt: `${proposalBundle.createdAt.toLocaleString("en-US", {
        timeZone: UseEastern ? EasternTimeZone : UserTimeZone,
        dateStyle: "short",
        timeStyle: "short",
      })}${timeSuffix}`,
      updatedAt: `${proposalBundle.updatedAt.toLocaleString("en-US", {
        timeZone: UseEastern ? EasternTimeZone : UserTimeZone,
        dateStyle: "short",
        timeStyle: "short",
      })}${timeSuffix}`,
      expiresAt:
        proposalBundle.expiresAt === null
          ? "Never"
          : `${proposalBundle.expiresAt.toLocaleString("en-US", {
              timeZone: UseEastern ? EasternTimeZone : UserTimeZone,
              dateStyle: "short",
              timeStyle: "short",
            })}${timeSuffix}`,
      tradeProposalsCount:
        proposalBundle.tradeProposals.length.toLocaleString("en-US"),
      status: {
        value: (
          <>
            <span>{proposalBundle.status}</span>
          </>
        ),
      },
    }));
  }, [proposalBundles.data, history, proposalFilter, page]);

  if (proposalBundles.isLoading || proposalBundles.hasError) {
    return (
      <Stack direction={"column"} gap={2}>
        <Stack
          direction={"row"}
          gap={2}
          justifyContent="space-between"
          alignItems="center"
        >
          <Select
            value={proposalFilter}
            onChange={setProposalFilter}
            options={BundleFilterOptions}
            renderValue={(value) => value}
          />

          <div>
            Updated at{" "}
            {updatedAt === null ? "Not yet" : format(updatedAt, "PPpp")} (local)
          </div>
        </Stack>

        <PaginatedTable
          key="loading"
          isLoading={true}
          columns={columns}
          rows={[]}
          pagination={{
            currentPage: page,
            totalPages: 1,
            pageSize: pageSize,
            totalCount: 0,
          }}
          onPageChange={() => {}}
        />
      </Stack>
    );
  }

  const totalPages = Math.ceil(
    proposalBundles.data.pagination.totalCount /
      proposalBundles.data.pagination.pageSize
  );

  return (
    <Stack direction={"column"} gap={2}>
      <Stack
        direction={"row"}
        gap={2}
        justifyContent="space-between"
        alignItems="center"
      >
        <Select
          value={proposalFilter}
          onChange={setProposalFilter}
          options={BundleFilterOptions}
          renderValue={(value) => value}
        />

        <div>
          Updated at{" "}
          {updatedAt === null ? "Not yet" : format(updatedAt, "PPpp")} (local)
        </div>
      </Stack>

      <PaginatedTable
        key="loaded"
        isLoading={false}
        columns={columns}
        rows={data}
        pagination={{
          currentPage: page,
          totalPages: totalPages,
          pageSize: proposalBundles.data.pagination.pageSize,
          totalCount: proposalBundles.data.pagination.totalCount,
          prevPage: page === 1 ? undefined : page - 1,
          nextPage: page === totalPages ? undefined : page + 1,
        }}
        onPageChange={(newPage) => {
          setPage(newPage);
        }}
      />
    </Stack>
  );
};

export default ProposalBundlesTable;
